import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tab } from '@headlessui/react';
import { ArrowRightOnRectangleIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const SRV_URL = "https://app.daftardecor.com";

type TimeFilter = '24h' | '7d' | '30d' | 'all';
type ProjectStatusFilter = 'all' | 'submitted' | 'in_progress';
type ProjectStageFilter = 'all' | 'OPEN' | 'CONTACTED' | 'PREPARING' | 'SIGNED' | 'LOST';

interface User {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  emailVerified: boolean;
  phoneVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Project {
  id: string;
  kind: string | null;
  companyName: string | null;
  website: string | null;
  location: string | null;
  carpetArea: number | null;
  submitted: boolean;
  stage: string;
  createdAt: string;
  updatedAt: string;
  user: User;
}

interface Lead {
  id: string;
  name: string | null;
  companyName: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  message: string | null;
  whatsappOK: boolean;
  carpetArea: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Partnership {
  id: string;
  name: string | null;
  companyName: string | null;
  email: string | null;
  phone: string | null;
  comments: string | null;
  city: string | null;
  purpose: string | null;
  upload: string | null;
  createdAt: string;
  updatedAt: string;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

const AdminDashboard: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  // Filter states
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [projectStatusFilter, setProjectStatusFilter] = useState<ProjectStatusFilter>('all');
  const [projectStageFilter, setProjectStageFilter] = useState<ProjectStageFilter>('all');

  // Pagination states
  const [projectsPage, setProjectsPage] = useState(1);
  const [leadsPage, setLeadsPage] = useState(1);
  const [partnershipsPage, setPartnershipsPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('adminToken');

    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };

      const [projectsRes, leadsRes, partnershipsRes] = await Promise.all([
        axios.get(`${SRV_URL}/api/admin/projects`, config),
        axios.get(`${SRV_URL}/api/admin/leads`, config),
        axios.get(`${SRV_URL}/api/admin/partnerships`, config)
      ]);

      console.log('Projects Response:', projectsRes.data);
      console.log('Leads Response:', leadsRes.data);
      console.log('Partnerships Response:', partnershipsRes.data);

      setProjects(projectsRes.data.projects || []);
      setLeads(leadsRes.data.leads || []);
      setPartnerships(partnershipsRes.data.partnerships || []);
    } catch (error: any) {
      console.error('Error fetching admin data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Time filter helper
  const filterByTime = <T extends { createdAt: string }>(items: T[]): T[] => {
    if (timeFilter === 'all') return items;

    const now = new Date();
    const cutoff = new Date();

    switch (timeFilter) {
      case '24h':
        cutoff.setHours(now.getHours() - 24);
        break;
      case '7d':
        cutoff.setDate(now.getDate() - 7);
        break;
      case '30d':
        cutoff.setDate(now.getDate() - 30);
        break;
    }

    return items.filter(item => new Date(item.createdAt) >= cutoff);
  };

  // Filtered data
  const filteredProjects = useMemo(() => {
    let filtered = filterByTime(projects);

    if (projectStatusFilter !== 'all') {
      filtered = filtered.filter(p =>
        projectStatusFilter === 'submitted' ? p.submitted : !p.submitted
      );
    }

    if (projectStageFilter !== 'all') {
      filtered = filtered.filter(p => p.stage === projectStageFilter);
    }

    return filtered;
  }, [projects, timeFilter, projectStatusFilter, projectStageFilter]);

  const filteredLeads = useMemo(() => filterByTime(leads), [leads, timeFilter]);
  const filteredPartnerships = useMemo(() => filterByTime(partnerships), [partnerships, timeFilter]);

  // Paginated data
  const paginatedProjects = useMemo(() => {
    const start = (projectsPage - 1) * pageSize;
    return filteredProjects.slice(start, start + pageSize);
  }, [filteredProjects, projectsPage, pageSize]);

  const paginatedLeads = useMemo(() => {
    const start = (leadsPage - 1) * pageSize;
    return filteredLeads.slice(start, start + pageSize);
  }, [filteredLeads, leadsPage, pageSize]);

  const paginatedPartnerships = useMemo(() => {
    const start = (partnershipsPage - 1) * pageSize;
    return filteredPartnerships.slice(start, start + pageSize);
  }, [filteredPartnerships, partnershipsPage, pageSize]);

  // Total pages
  const projectsTotalPages = Math.ceil(filteredProjects.length / pageSize);
  const leadsTotalPages = Math.ceil(filteredLeads.length / pageSize);
  const partnershipsTotalPages = Math.ceil(filteredPartnerships.length / pageSize);

  // Reset to page 1 when filters change
  useEffect(() => {
    setProjectsPage(1);
    setLeadsPage(1);
    setPartnershipsPage(1);
  }, [timeFilter, projectStatusFilter, projectStageFilter, pageSize]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="mt-2 text-gray-600">Manage projects, leads, and partnerships</p>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
            Logout
          </button>
        </div>

        {/* Time Filter Buttons */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Time Period</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setTimeFilter('24h')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                timeFilter === '24h'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Last 24 Hours
            </button>
            <button
              onClick={() => setTimeFilter('7d')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                timeFilter === '7d'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Last 7 Days
            </button>
            <button
              onClick={() => setTimeFilter('30d')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                timeFilter === '30d'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Last 30 Days
            </button>
            <button
              onClick={() => setTimeFilter('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                timeFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Time
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900">Total Projects</h3>
              <p className="mt-2 text-3xl font-bold text-blue-600">{filteredProjects.length}</p>
              <p className="mt-1 text-sm text-blue-700">
                {filteredProjects.filter(p => p.submitted).length} submitted
              </p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-green-900">Total Leads</h3>
              <p className="mt-2 text-3xl font-bold text-green-600">{filteredLeads.length}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-purple-900">Total Partnerships</h3>
              <p className="mt-2 text-3xl font-bold text-purple-600">{filteredPartnerships.length}</p>
            </div>
          </div>
        </div>

        <Tab.Group>
          <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1">
            <Tab
              className={({ selected }) =>
                classNames(
                  'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                  'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-white text-blue-700 shadow'
                    : 'text-blue-900 hover:bg-white/[0.12] hover:text-blue-800'
                )
              }
            >
              Projects ({projects.length})
            </Tab>
            <Tab
              className={({ selected }) =>
                classNames(
                  'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                  'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-white text-blue-700 shadow'
                    : 'text-blue-900 hover:bg-white/[0.12] hover:text-blue-800'
                )
              }
            >
              Leads ({leads.length})
            </Tab>
            <Tab
              className={({ selected }) =>
                classNames(
                  'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                  'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-white text-blue-700 shadow'
                    : 'text-blue-900 hover:bg-white/[0.12] hover:text-blue-800'
                )
              }
            >
              Partnerships ({partnerships.length})
            </Tab>
          </Tab.List>
          <Tab.Panels className="mt-4">
            {/* Projects Tab */}
            <Tab.Panel className="rounded-xl bg-white p-3">
              {/* Project Filters */}
              <div className="mb-4 flex flex-wrap gap-4 items-center">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={projectStatusFilter}
                    onChange={(e) => setProjectStatusFilter(e.target.value as ProjectStatusFilter)}
                    className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="all">All</option>
                    <option value="submitted">Submitted</option>
                    <option value="in_progress">In Progress</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                  <select
                    value={projectStageFilter}
                    onChange={(e) => setProjectStageFilter(e.target.value as ProjectStageFilter)}
                    className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="all">All</option>
                    <option value="OPEN">Open</option>
                    <option value="CONTACTED">Contacted</option>
                    <option value="PREPARING">Preparing</option>
                    <option value="SIGNED">Signed</option>
                    <option value="LOST">Lost</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Per Page</label>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="block w-24 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Area (sqft)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stage
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedProjects.map((project) => (
                      <tr key={project.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{project.companyName || '-'}</div>
                          {project.website && (
                            <div className="text-sm text-gray-500">{project.website}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{project.user.name || '-'}</div>
                          <div className="text-sm text-gray-500">{project.user.phone || project.user.email || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {project.location || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {project.carpetArea?.toLocaleString() || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            project.submitted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {project.submitted ? 'Submitted' : 'In Progress'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {project.stage}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(project.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <a
                            href={`/project/${project.id}/summary`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            View
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredProjects.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No projects found
                  </div>
                )}
              </div>
              {/* Pagination for Projects */}
              {filteredProjects.length > 0 && (
                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <button
                      onClick={() => setProjectsPage(prev => Math.max(prev - 1, 1))}
                      disabled={projectsPage === 1}
                      className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setProjectsPage(prev => Math.min(prev + 1, projectsTotalPages))}
                      disabled={projectsPage === projectsTotalPages}
                      className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{(projectsPage - 1) * pageSize + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(projectsPage * pageSize, filteredProjects.length)}</span> of{' '}
                        <span className="font-medium">{filteredProjects.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                          onClick={() => setProjectsPage(prev => Math.max(prev - 1, 1))}
                          disabled={projectsPage === 1}
                          className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                        >
                          <ChevronLeftIcon className="h-5 w-5" />
                        </button>
                        <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300">
                          Page {projectsPage} of {projectsTotalPages}
                        </span>
                        <button
                          onClick={() => setProjectsPage(prev => Math.min(prev + 1, projectsTotalPages))}
                          disabled={projectsPage === projectsTotalPages}
                          className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                        >
                          <ChevronRightIcon className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </Tab.Panel>

            {/* Leads Tab */}
            <Tab.Panel className="rounded-xl bg-white p-3">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        City
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Area (sqft)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Message
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        WhatsApp
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedLeads.map((lead) => (
                      <tr key={lead.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {lead.name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {lead.companyName || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{lead.email || '-'}</div>
                          <div className="text-sm text-gray-500">{lead.phone || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {lead.city || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {lead.carpetArea || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {lead.message || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            lead.whatsappOK ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {lead.whatsappOK ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(lead.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredLeads.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No leads found
                  </div>
                )}
              </div>
              {/* Pagination for Leads */}
              {filteredLeads.length > 0 && (
                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <button
                      onClick={() => setLeadsPage(prev => Math.max(prev - 1, 1))}
                      disabled={leadsPage === 1}
                      className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setLeadsPage(prev => Math.min(prev + 1, leadsTotalPages))}
                      disabled={leadsPage === leadsTotalPages}
                      className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{(leadsPage - 1) * pageSize + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(leadsPage * pageSize, filteredLeads.length)}</span> of{' '}
                        <span className="font-medium">{filteredLeads.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                          onClick={() => setLeadsPage(prev => Math.max(prev - 1, 1))}
                          disabled={leadsPage === 1}
                          className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                        >
                          <ChevronLeftIcon className="h-5 w-5" />
                        </button>
                        <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300">
                          Page {leadsPage} of {leadsTotalPages}
                        </span>
                        <button
                          onClick={() => setLeadsPage(prev => Math.min(prev + 1, leadsTotalPages))}
                          disabled={leadsPage === leadsTotalPages}
                          className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                        >
                          <ChevronRightIcon className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </Tab.Panel>

            {/* Partnerships Tab */}
            <Tab.Panel className="rounded-xl bg-white p-3">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        City
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Purpose
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Comments
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Upload
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedPartnerships.map((partnership) => (
                      <tr key={partnership.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {partnership.name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {partnership.companyName || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{partnership.email || '-'}</div>
                          <div className="text-sm text-gray-500">{partnership.phone || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {partnership.city || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {partnership.purpose || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {partnership.comments || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {partnership.upload ? (
                            <a
                              href={partnership.upload}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-900"
                            >
                              View File
                            </a>
                          ) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(partnership.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredPartnerships.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No partnerships found
                  </div>
                )}
              </div>
              {/* Pagination for Partnerships */}
              {filteredPartnerships.length > 0 && (
                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <button
                      onClick={() => setPartnershipsPage(prev => Math.max(prev - 1, 1))}
                      disabled={partnershipsPage === 1}
                      className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPartnershipsPage(prev => Math.min(prev + 1, partnershipsTotalPages))}
                      disabled={partnershipsPage === partnershipsTotalPages}
                      className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{(partnershipsPage - 1) * pageSize + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(partnershipsPage * pageSize, filteredPartnerships.length)}</span> of{' '}
                        <span className="font-medium">{filteredPartnerships.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                          onClick={() => setPartnershipsPage(prev => Math.max(prev - 1, 1))}
                          disabled={partnershipsPage === 1}
                          className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                        >
                          <ChevronLeftIcon className="h-5 w-5" />
                        </button>
                        <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300">
                          Page {partnershipsPage} of {partnershipsTotalPages}
                        </span>
                        <button
                          onClick={() => setPartnershipsPage(prev => Math.min(prev + 1, partnershipsTotalPages))}
                          disabled={partnershipsPage === partnershipsTotalPages}
                          className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                        >
                          <ChevronRightIcon className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  );
};

export default AdminDashboard;
